"use client";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import {
  addHours,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from "date-fns";

import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  ArrowTrendingUpIcon,
} from "@heroicons/react/24/outline";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getInsertionTargets, positionInsertionTargets, nearestInsertionTarget, nearestCalendarBand, type CalendarPlacementTarget } from "./calendar/insertionTargets";
import { useNow } from "../hooks/useNow";
import toast from "react-hot-toast";
import { ModalContext } from "../hooks/useModal";
import { useReadPublications } from "../hooks/usePublications";
import { PublicationModel } from "../types/models";
import {
  CALENDAR_SLOTS,
  createOccupiedSlotSet,
  findNextRecommendedTimeSlot,
  getISODay,
  type TimeSlotDefinition,
} from "../utils/publicationDistribution";
import { toValidDate } from "../utils/date";
import Button, { BUTTON_STYLE } from "./Button";
import {
  onScrollSettled,
  planTileReveal,
  prefersReducedMotion,
  revealCalendarTile,
  type CalendarRevealRequest,
  type TileRevealBand,
} from "./calendar/revealCalendarTile";
import PublicationItem from "./PublicationItem";
import CalendarViewportFrame from "./calendar/CalendarViewportFrame";
import { CALENDAR_FRAME_BOTTOM_GAP } from "./calendar/calendarFrame";
import type { CalendarPublicationContext } from "./publicationFlow/publicationContext";
import SelectVideoPublicationForm from "./SelectVideoPublicationForm";
import Typography from "./Typography";
import { useMediaQuery } from "usehooks-ts";

/**
 * Registers a slot cell for uniform row sizing. Returns an unregister
 * callback. Cells are grouped into rows (one slot band of one week); every
 * cell in a row shares one minHeight — the natural content height of the
 * row's most crowded cell — so the band lines up across the week. Since each
 * cell's content includes only its cards and compact padding. Bands unused
 * across a whole week keep a small, usable pointer target.
 */
type SlotCellRegistration = (
  rowKey: string,
  cellKey: string,
  section: HTMLElement,
  content: HTMLElement,
) => () => void;

function useUniformSlotCells(): SlotCellRegistration {
  type Cell = { section: HTMLElement; content: HTMLElement };
  const rowsRef = useRef<Map<string, Map<string, Cell>>>(new Map());
  const frameRef = useRef<number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const syncAll = useCallback(() => {
    rowsRef.current.forEach((cells) => {
      let maxContentHeight = 0;
      cells.forEach(({ content }) => {
        maxContentHeight = Math.max(maxContentHeight, content.offsetHeight);
      });
      const minHeight = `${Math.max(32, maxContentHeight)}px`;
      cells.forEach(({ section }) => {
        section.style.minHeight = minHeight;
      });
    });
  }, []);

  const scheduleSync = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      syncAll();
    });
  }, [syncAll]);

  const registerSlotCell = useCallback<SlotCellRegistration>(
    (rowKey, cellKey, section, content) => {
      if (!observerRef.current) {
        observerRef.current = new ResizeObserver(() => scheduleSync());
      }

      let cells = rowsRef.current.get(rowKey);
      if (!cells) {
        cells = new Map();
        rowsRef.current.set(rowKey, cells);
      }
      cells.set(cellKey, { section, content });
      observerRef.current.observe(content);
      syncAll();

      return () => {
        observerRef.current?.unobserve(content);
        const rowCells = rowsRef.current.get(rowKey);
        if (rowCells) {
          rowCells.delete(cellKey);
          if (rowCells.size === 0) rowsRef.current.delete(rowKey);
        }
        // Let the cell collapse back to its natural height once it no longer
        // takes part in uniform sizing (e.g. its band emptied for the week)
        section.style.minHeight = "";
        scheduleSync();
      };
    },
    [scheduleSync, syncAll],
  );

  useLayoutEffect(
    () => () => {
      // Fully reset so a StrictMode (or real) remount starts from a clean
      // slate: a stale frameRef would permanently block scheduleSync's
      // "already scheduled" gate, and a disconnected observer would never
      // re-observe surviving cells.
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    },
    [],
  );

  return registerSlotCell;
}


interface TimeSlotProps {
  day: Date;
  slot: TimeSlotDefinition;
  isOver: boolean;
  setNodeRef: (node: HTMLElement | null) => void;
  publicationsInSlot: PublicationModel[];
  draggingPublicationId: string | null;
  isDragging: boolean;
  isDayHovered: boolean;
  className?: string;
  isPublicationDraggable: (publication: PublicationModel) => boolean;
  isPublicationDisabled: (publication: PublicationModel) => boolean;
  setPublicationRef: (
    publicationId: string,
    element: HTMLElement | null,
  ) => void;
  showTimeSlotsOnHover: boolean;
  isBandActiveInWeek: boolean;
  isLastSlot: boolean;
  registerTargetResolver: (resolver: ((clientY: number) => CalendarPlacementTarget | null) | null) => void;
}

function TimeSlot({
  day, slot, isOver, setNodeRef, publicationsInSlot, draggingPublicationId,
  isDragging, isDayHovered, className,
  isPublicationDraggable, isPublicationDisabled, setPublicationRef,
  isBandActiveInWeek, isLastSlot, registerTargetResolver,
}: TimeSlotProps) {
  const { draggingPublication, registerSlotCell, publicationIndexById, dragTarget } = useContext(CalendarContext);
  const now = useNow();
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const dayKey = format(day, "yyyy-MM-dd");
  const weekKey = format(startOfWeek(day, { weekStartsOn: 1 }), "yyyy-MM-dd");
  const slotKey = `${dayKey}-${slot.id}`;
  const isHiddenSlot = slot.isDisabled && publicationsInSlot.length === 0;
  const orderOf = (id: string) => publicationIndexById?.get(id) ?? Infinity;
  const sorted = [...publicationsInSlot].sort((a, b) =>
    new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime() || orderOf(a.id) - orderOf(b.id));
  const remaining = sorted.filter(pub => pub.id !== draggingPublicationId);
  const insertionCards = remaining.map(pub => ({ id: pub.id, time: new Date(pub.scheduledAt).getTime(), order: orderOf(pub.id) }));
  const sourceOrder = draggingPublicationId ? orderOf(draggingPublicationId) : Infinity;
  const setSectionRef = useCallback((node: HTMLDivElement | null) => {
    sectionRef.current = node;
    setNodeRef(node);
  }, [setNodeRef]);

  useLayoutEffect(() => {
    if (!registerSlotCell || isHiddenSlot || !isBandActiveInWeek || !sectionRef.current || !contentRef.current) return;
    return registerSlotCell(`${weekKey}|${slot.id}`, dayKey, sectionRef.current, contentRef.current);
  }, [registerSlotCell, isHiddenSlot, isBandActiveInWeek, weekKey, dayKey, slot.id]);

  const resolveTarget = (clientY: number): CalendarPlacementTarget | null => {
    const section = sectionRef.current;
    if (!section || slot.isDisabled) return null;
    const rect = section.getBoundingClientRect();
    const elements = [...section.querySelectorAll<HTMLElement>("[data-calendar-post]")];
    const source = elements.find(el => el.dataset.calendarPost === draggingPublicationId);
    if (source && draggingPublication?.scheduledAt) {
      const sourceRect = source.getBoundingClientRect();
      if (clientY >= sourceRect.top && clientY <= sourceRect.bottom) {
        return { slotKey, time: new Date(draggingPublication.scheduledAt).getTime(), top: sourceRect.top - rect.top, original: true };
      }
    }
    const geometry = remaining.map(pub => {
      const card = elements.find(el => el.dataset.calendarPost === pub.id)!;
      const bounds = card.getBoundingClientRect();
      return { top: bounds.top - rect.top, bottom: bounds.bottom - rect.top };
    });
    const header = section.querySelector<HTMLElement>("[data-slot-header]");
    const start = header ? header.getBoundingClientRect().bottom - rect.top : 0;
    const candidates = getInsertionTargets({ day, slot, cards: insertionCards, now: now.getTime(), sourceOrder });
    const positioned = positionInsertionTargets(candidates, geometry, start, rect.height - 4);
    const nearest = nearestInsertionTarget(positioned, clientY - rect.top);
    return nearest ? { slotKey, time: nearest.time, top: nearest.top } : null;
  };
  useLayoutEffect(() => {
    registerTargetResolver(resolveTarget);
    return () => registerTargetResolver(null);
  });

  if (isHiddenSlot) return null;
  const target = isDragging && isOver && dragTarget?.slotKey === slotKey ? dragTarget : null;
  const isActiveDragTarget = isDragging && !!target && !target.original;
  // Use the actual drop suggestions: a partially elapsed band may still
  // offer a future time. Invisible headers retain their layout space.
  const showHeader = isDragging && (isDayHovered || isOver) &&
    getInsertionTargets({ day, slot, cards: insertionCards, now: now.getTime(), sourceOrder }).length > 0;
  return (
    <div ref={setSectionRef} data-calendar-slot={slot.id} data-calendar-slot-key={slotKey}
      className={`group min-h-[32px] max-w-full relative ${className ?? ""}`}
    >
      <div ref={contentRef} className={`pt-0.5 ${isLastSlot ? "" : "pb-2"}`}>
        <div data-slot-header aria-hidden={!showHeader}
          data-calendar-drop-target={isActiveDragTarget ? "" : undefined}
          data-drop-time={isActiveDragTarget ? new Date(target.time).toISOString() : undefined}
          className={`flex items-center justify-between gap-1 pointer-events-none ${showHeader ? "opacity-100" : "opacity-0"}`}>
          <Typography variant="overline" className={`normal-case shrink-0 ${isActiveDragTarget ? "text-blue-500" : "text-slate-400"}`}>{slot.label}</Typography>
          {slot.isRecommended(getISODay(day)) && <ArrowTrendingUpIcon className="w-4 h-4 text-green-500" />}
        </div>
        <div className="space-y-1">
          {sorted.map(publication => (
            <div key={publication.id} data-calendar-post={publication.id}>
              <PublicationItem publication={publication}
                isDraggable={isPublicationDraggable(publication)} isDisabled={isPublicationDisabled(publication)}
                isBeingDragged={publication.id === draggingPublicationId} onRefSet={setPublicationRef} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface DroppableTimeSlotProps {
  day: Date;
  slot: TimeSlotDefinition;
  publicationsInSlot: PublicationModel[];
  draggingPublicationId: string | null;
  isDragging: boolean;
  isDayHovered: boolean;
  className?: string;
  isPublicationDraggable: (publication: PublicationModel) => boolean;
  isPublicationDisabled: (publication: PublicationModel) => boolean;
  setPublicationRef: (
    publicationId: string,
    element: HTMLElement | null,
  ) => void;
  showTimeSlotsOnHover: boolean;
  isBandActiveInWeek: boolean;
  isLastSlot: boolean;
  /** Agenda now-bar must stay inside the content without bleeding past
   *  the day edge (the grid offsets are tuned for column cells and overflow
   *  the phone agenda). */
  layout?: "grid" | "agenda";
}

function DroppableTimeSlot({
  day,
  slot,
  publicationsInSlot,
  draggingPublicationId,
  isDragging,
  isDayHovered,
  className,
  isPublicationDraggable,
  isPublicationDisabled,
  setPublicationRef,
  showTimeSlotsOnHover,
  isBandActiveInWeek,
  isLastSlot,
  layout = "grid",
}: DroppableTimeSlotProps) {
  const targetResolverRef = useRef<((clientY: number) => CalendarPlacementTarget | null) | null>(null);
  const resolveTarget = useCallback((clientY: number) => targetResolverRef.current?.(clientY) ?? null, []);
  const registerTargetResolver = useCallback((resolver: typeof targetResolverRef.current) => { targetResolverRef.current = resolver; }, []);
  const { setNodeRef, isOver, node } = useDroppable({
    id: `${format(day, "yyyy-MM-dd")}-${slot.id}`,
    data: {
      day,
      slot,
      isTimeSlot: true,
      resolveTarget,
      publicationsInSlot,
    },
  });
  const now = useNow();
  const nowMs = now.getTime();
  // A slot spans [startHour, endHour] inclusive, i.e. up to the start of
  // endHour + 1 (Morning 6-9 runs 06:00-09:59).
  const slotStartMs = addHours(startOfDay(day), slot.startHour).getTime();
  const slotEndMs = addHours(startOfDay(day), slot.endHour + 1).getTime();
  const isCurrentSlot = nowMs >= slotStartMs && nowMs < slotEndMs;

  // Cards in this slot, in render order, so the now bar can anchor to them.
  const cardRefs = useRef<Map<string, HTMLElement>>(new Map());
  const setCardRef = useCallback(
    (publicationId: string, element: HTMLElement | null) => {
      if (element) cardRefs.current.set(publicationId, element);
      else cardRefs.current.delete(publicationId);
      setPublicationRef(publicationId, element);
    },
    [setPublicationRef],
  );
  const cardAnchors = useMemo(
    () =>
      publicationsInSlot
        .filter((publication) => publication.scheduledAt)
        .map((publication) => ({
          id: publication.id,
          time: new Date(publication.scheduledAt).getTime(),
        }))
        .sort((a, b) => a.time - b.time),
    [publicationsInSlot],
  );

  // The bar's vertical position is not a fraction of the slot's pixel height
  // (a slot's height reflects how many cards it holds, not elapsed time).
  // Instead the cards split the slot into time segments: [slot start → first
  // card], [card → next card], ..., [last card → slot end]. The bar lands in
  // the segment containing "now", proportionally within that segment's
  // pixels, so it always sits below past cards and above upcoming ones.
  const [nowBarTop, setNowBarTop] = useState<number | null>(null);
  useLayoutEffect(() => {
    const section = node.current;
    if (!isCurrentSlot || !section) {
      setNowBarTop(null);
      return;
    }
    const measure = () => {
      const sectionRect = section.getBoundingClientRect();
      const header = section.querySelector<HTMLElement>("[data-slot-header]");
      let segmentStartMs = slotStartMs;
      let segmentTop = header
        ? header.getBoundingClientRect().bottom - sectionRect.top
        : 0;
      let segmentEndMs = slotEndMs;
      let segmentBottom = sectionRect.height;
      for (const anchor of cardAnchors) {
        const card = cardRefs.current.get(anchor.id);
        if (!card) continue;
        const cardRect = card.getBoundingClientRect();
        if (anchor.time <= nowMs) {
          // Past card: the bar goes below it.
          segmentStartMs = anchor.time;
          segmentTop = cardRect.bottom - sectionRect.top;
        } else {
          // First upcoming card: the bar stays above it.
          segmentEndMs = anchor.time;
          segmentBottom = cardRect.top - sectionRect.top;
          break;
        }
      }
      const span = segmentEndMs - segmentStartMs;
      const fraction =
        span > 0 ? Math.min(1, Math.max(0, (nowMs - segmentStartMs) / span)) : 0;
      const height = Math.max(0, segmentBottom - segmentTop);
      setNowBarTop(segmentTop + fraction * height);
    };
    measure();
    // Slot heights settle asynchronously (band alignment across the week,
    // cards mounting), so re-measure whenever the section resizes.
    const observer = new ResizeObserver(measure);
    observer.observe(section);
    return () => observer.disconnect();
  }, [isCurrentSlot, nowMs, slotStartMs, slotEndMs, cardAnchors, node]);

  // The dot sits on the frame's side border, which curves inward in the
  // sticky caps' 10px corner zones. Any mask there cuts the dot into an odd
  // shape, so hide it whole while it overlaps either zone; the line itself
  // is masked by the caps like the rest of the content.
  const nowDotRef = useRef<HTMLDivElement>(null);
  const hasNowBar = isCurrentSlot && nowBarTop !== null;
  useLayoutEffect(() => {
    const dot = nowDotRef.current;
    const frame = dot?.closest<HTMLElement>(':has(> [data-calendar-frame="top"])');
    const topEdge = frame?.querySelector<HTMLElement>(':scope > [data-calendar-frame="top"]');
    const bottomEdge = frame?.querySelector<HTMLElement>(':scope > [data-calendar-frame="bottom"]');
    if (!dot || !topEdge || !bottomEdge) return;
    const CORNER = 10;
    const update = () => {
      const rect = dot.getBoundingClientRect();
      const inCorner =
        rect.top < topEdge.getBoundingClientRect().top + CORNER ||
        rect.bottom > bottomEdge.getBoundingClientRect().top - CORNER;
      dot.style.visibility = inCorner ? "hidden" : "";
    };
    update();
    // Capture sees both the document and the schedule workspace scroller.
    document.addEventListener("scroll", update, { capture: true, passive: true });
    window.addEventListener("resize", update);
    return () => {
      document.removeEventListener("scroll", update, { capture: true });
      window.removeEventListener("resize", update);
    };
  }, [hasNowBar, nowBarTop]);

  return (
    <div className="relative min-w-0 max-w-full">
      <TimeSlot
        registerTargetResolver={registerTargetResolver}
        day={day}
        slot={slot}
        isOver={isOver}
        setNodeRef={setNodeRef}
        publicationsInSlot={publicationsInSlot}
        draggingPublicationId={draggingPublicationId}
        isDragging={isDragging}
        isDayHovered={isDayHovered}
        className={className}
        isPublicationDraggable={isPublicationDraggable}
        isPublicationDisabled={isPublicationDisabled}
        setPublicationRef={setCardRef}
        showTimeSlotsOnHover={showTimeSlotsOnHover}
        isBandActiveInWeek={isBandActiveInWeek}
        isLastSlot={isLastSlot}
      />
      {hasNowBar && (
        <div
          // Under the frame's sticky caps (z-10) and header (z-20), so it
          // never shows past the frame edge once scrolled under them.
          className="absolute z-[5] pointer-events-none"
          style={{
            top: `${nowBarTop}px`,
            // Span the day-cell padding from the center of its left border,
            // where the dot is centered. The agenda stops at the frame's inner
            // edge so nothing overflows right into the shell's scrollport.
            ...(layout === "agenda"
              ? { left: "-12.5px", right: "-12px" }
              : { left: "-8.5px", right: "-9px" }),
          }}
        >
          <div className="w-full h-0.5 bg-blue-500 relative">
            <div ref={nowDotRef} className="absolute -left-1 top-[-3px] w-2 h-2 rounded-full bg-blue-500 border border-slate-50"></div>
          </div>
        </div>
      )}
    </div>
  );
}

interface DroppableDayProps {
  day: Date;
  dayId: string;
  dayPublications: PublicationModel[];
  isCurrentMonth: boolean;
  draggingPublicationId: string | null;
  isDragging: boolean;
  id?: string;
  allowCreatePublication: boolean;
  isPublicationDraggable: (publication: PublicationModel) => boolean;
  isPublicationDisabled: (publication: PublicationModel) => boolean;
  setPublicationRef: (
    publicationId: string,
    element: HTMLElement | null,
  ) => void;
  setDayRef: (dayId: string, element: HTMLDivElement | null) => void;
  isDayDragOver: boolean;
  showTimeSlotsOnHover: boolean;
  weekActiveSlotIds?: Set<string>;
  /** `grid` is the desktop month/week columns; `agenda` is the mobile
   *  vertical day list — compact empty days, no cross-column band stretch. */
  layout?: "grid" | "agenda";
}

function DroppableDay({
  day,
  dayId,
  dayPublications,
  isCurrentMonth,
  draggingPublicationId,
  isDragging,
  id,
  allowCreatePublication,
  isPublicationDraggable,
  isPublicationDisabled,
  setPublicationRef,
  setDayRef,
  isDayDragOver,
  showTimeSlotsOnHover,
  weekActiveSlotIds,
  layout = "grid",
}: DroppableDayProps) {
  const ref = useRef<HTMLDivElement>(null);
  const currentTime = useNow();
  const { onCreatePublicationOnDay, canMovePublications } = useContext(CalendarContext);
  const setDayRootRef = useCallback(
    (element: HTMLDivElement | null) => {
      setDayRef(dayId, element);
    },
    [dayId, setDayRef],
  );
  const unscheduledPublications = dayPublications.filter(
    (publication) => !publication.scheduledAt,
  );

  // Update slot height whenever publications change

  const publicationsInTimeSlots = useMemo(() => {
    const slots = new Map<string, PublicationModel[]>();
    CALENDAR_SLOTS.forEach((slot) => slots.set(slot.id, []));

    dayPublications.forEach((publication) => {
      if (!publication.scheduledAt) return;

      const hour = new Date(publication.scheduledAt).getHours();
      const slot = CALENDAR_SLOTS.find(
        (slot) => hour >= slot.startHour && hour <= slot.endHour,
      );

      if (slot) {
        slots.get(slot.id)?.push(publication);
      }
    });

    return slots;
  }, [dayPublications]);

  const isToday = isSameDay(day, currentTime);
  const isAgenda = layout === "agenda";

  // Editable agendas retain compact empty drop targets so lifting a card
  // does not change day heights. Read-only agendas show occupied bands.
  const slotsToRender = isAgenda
    ? CALENDAR_SLOTS.filter((slot) => {
        const pubs = publicationsInTimeSlots.get(slot.id) || [];
        if (pubs.length > 0) return true;
        if (slot.isDisabled) return false;
        return canMovePublications;
      })
    : CALENDAR_SLOTS;

  const canCreate = allowCreatePublication && !!onCreatePublicationOnDay && !isDragging &&
    startOfDay(day).getTime() >= startOfDay(currentTime).getTime();
  const createOnDay = () => {
    if (canCreate) onCreatePublicationOnDay?.(startOfDay(day));
  };
  const handleEmptySpaceClick: React.MouseEventHandler<HTMLDivElement> = event => {
    // Cards and controls keep their own actions. Empty space selects the
    // day, regardless of band height or the pointer's vertical position.
    if ((event.target as Element).closest("[data-publication-id], button, a, input, select, textarea, [role=button]")) return;
    createOnDay();
  };
  const dayAddButton = canCreate ? (
    <Button variant="tertiary" size="s" className="!p-1 opacity-0 group-hover/day:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100" aria-label={`Add post on ${format(day, "MMMM d")}`}
      onClick={createOnDay}><PlusIcon className="w-4 h-4 text-slate-400" /></Button>
  ) : null;

  const dayBody = (
    <>
      {isAgenda ? (
        <div className="flex items-center gap-2 mb-2">
          {dayAddButton}
          <Typography variant="subtitle2" className="ml-auto text-right text-slate-700">
            {format(day, "EEE")}
          </Typography>
          <Typography
            variant="subtitle2"
            component="div"
            className={`w-7 h-7 flex items-center justify-center rounded-full shrink-0 ${
              isToday ? "bg-blue-500 text-white" : ""
            }`}
          >
            {format(day, "d")}
          </Typography>
        </div>
      ) : (
        <div className="flex items-center gap-1 mb-1">
          {dayAddButton}
          <Typography
            variant="subtitle2"
            component="div"
            className={`ml-auto w-7 h-7 flex items-center justify-center rounded-full shrink-0 ${
              isToday ? "bg-blue-500 text-white" : ""
            } ${!isCurrentMonth ? "text-slate-400" : ""}`}
          >
            {format(day, "d")}
          </Typography>
        </div>
      )}

      <div
        className={`flex flex-col w-full relative ${isAgenda ? "" : "flex-1"}`}
        ref={ref}
      >
        {slotsToRender.map((slot, index) => {
          return (
            <DroppableTimeSlot
              key={`${format(day, "yyyy-MM-dd")}-${slot.id}`}
              day={day}
              slot={slot}
              publicationsInSlot={publicationsInTimeSlots.get(slot.id) || []}
              draggingPublicationId={draggingPublicationId}
              isDragging={isDragging}
              isDayHovered={isDayDragOver}
              className={
                !isAgenda && index === slotsToRender.length - 1 ? "flex-1" : ""
              }
              isPublicationDraggable={isPublicationDraggable}
              isPublicationDisabled={isPublicationDisabled}
              setPublicationRef={setPublicationRef}
              showTimeSlotsOnHover={showTimeSlotsOnHover}
              isBandActiveInWeek={
                isAgenda ? false : (weekActiveSlotIds?.has(slot.id) ?? false)
              }
              isLastSlot={index === slotsToRender.length - 1}
              layout={layout}
            />
          );
        })}

      </div>

      {unscheduledPublications.length > 0 && (
        <div className="space-y-1 mt-2 p-1 border border-dashed border-slate-150 rounded-md">
          <div className="text-xs text-slate-500 mb-1">Unscheduled</div>
          {unscheduledPublications.map((publication) => (
            <PublicationItem
              key={publication.id}
              publication={publication}
              isDraggable={isPublicationDraggable(publication)}
              isDisabled={isPublicationDisabled(publication)}
              isBeingDragged={publication.id === draggingPublicationId}
              onRefSet={setPublicationRef}
            />
          ))}
        </div>
      )}
    </>
  );

  // Agenda: border on the outer shell so day dividers span the full scroll
  // width; padding stays on an inner wrapper. Grid keeps the old cell chrome.
  if (isAgenda) {
    return (
      <div
        ref={setDayRootRef}
        className="group/day border-b border-slate-150 flex flex-col max-w-full"
        id={id}
        onClick={handleEmptySpaceClick}
      >
        <div className="px-3 py-3 flex flex-col min-w-0 max-w-full">
          {dayBody}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setDayRootRef}
      className={`group/day h-full p-2 border-r border-b border-slate-150 flex flex-col transition-colors duration-200 ${
        !isCurrentMonth ? "bg-white" : ""
      }`}
      id={id}
      onClick={handleEmptySpaceClick}
    >
      {dayBody}
    </div>
  );
}

interface CalendarContextType {
  draggingPublication: PublicationModel | null;
  dragTarget: CalendarPlacementTarget | null;
  onCreatePublicationOnDay: ((date: Date) => void) | null;
  canMovePublications: boolean;
  initialScheduledDate: Date | null;
  onPublicationClick:
    | ((publication: PublicationModel, element: HTMLElement | null) => void)
    | null;
  onPublicationHighlight: ((publication: PublicationModel) => void) | null;
  /** What a tile is to the host's session (batch / same project / other
   *  project) — the scheduler's calendar tab; null elsewhere. */
  getPublicationContext:
    | ((publication: PublicationModel) => CalendarPublicationContext)
    | null;
  highlightedAccountId: string | null;
  highlightedVideoId: string | null;
  highlightTrigger: number;
  isPublicationInActiveCalendarScope:
    | ((publication: PublicationModel) => boolean)
    | null;
  /** Schedule-workspace distinction between persisted and newly staged slots. */
  showSavedScheduledStatus:
    | ((publication: PublicationModel) => boolean)
    | null;
  /** The post the host's inspector is SHOWING — that tile wears the active
   *  surface. After a navigation step that is no longer the tile it was opened
   *  from. Null wherever the host has no inspector. */
  activePublicationId: string | null;
  /** The tile the grid has just brought into view for the inspector, and a
   *  counter so the same tile can pulse again. Deliberately separate from
   *  `highlightTrigger`: that one matches by short OR account and bumps every
   *  tile of either, and it is baked into the scheduler's Calendar `key`, so
   *  bumping it would remount the grid. */
  revealedPublicationId: string | null;
  revealPulseToken: number;
  registerSlotCell: SlotCellRegistration | null;
  /**
   * Position of each publication in the source list. Equal-time cards keep
   * this relative order when stacked (stable sort), so the drop preview uses
   * it to predict the exact post-drop position.
   */
  publicationIndexById: Map<string, number> | null;
  /**
   * True for the publication that was just drag-dropped. Its card remounts in
   * the new slot, and that remount must not replay the highlight bump.
   */
  isRecentlyDropped: ((publicationId: string) => boolean) | null;
}

export const CalendarContext = createContext<CalendarContextType>({
  draggingPublication: null,
  dragTarget: null,
  onCreatePublicationOnDay: null,
  canMovePublications: false,
  initialScheduledDate: null,
  onPublicationClick: null,
  onPublicationHighlight: null,
  getPublicationContext: null,
  highlightedAccountId: null,
  highlightedVideoId: null,
  highlightTrigger: 0,
  isPublicationInActiveCalendarScope: null,
  showSavedScheduledStatus: null,
  activePublicationId: null,
  revealedPublicationId: null,
  revealPulseToken: 0,
  registerSlotCell: null,
  publicationIndexById: null,
  isRecentlyDropped: null,
});

interface CalendarProps {
  viewMode?: "month" | "week";
  initialDate?: Date;
  initialPublicationId?: string;
  publications?: PublicationModel[];
  /** Hidden posts still occupy scheduling slots when a host filters the grid. */
  occupancyPublications?: PublicationModel[];
  isLoading?: boolean;
  isPublicationDraggable?: (publication: PublicationModel) => boolean;
  isPublicationDisabled?: (publication: PublicationModel) => boolean;
  /** §5.1 locked saved posts: dimmed with a lock glyph, never draggable,
   *  still clickable (a click focuses the short). */
  onPublicationScheduleChange?: (
    publication: PublicationModel,
    scheduledAt: Date,
  ) => Promise<void> | void;
  /** A tile's drag ACTIVATED — the pointer cleared the sensor's distance,
   *  not merely pressed the tile. A host keeping an inspector anchored to a
   *  tile closes it here, because nothing else will: the inspector dismisses
   *  on an outside click, and dnd-kit stops the click that follows an
   *  activated drag, so the panel would otherwise sit over the grid for the
   *  whole gesture. Not fired for a disabled tile, whose drag is cancelled
   *  before it moves anything. */
  onPublicationDragStart?: (publication: PublicationModel) => void;
  /** A tile was clicked, with its element (the popover's anchor). */
  onPublicationClick?: (
    publication: PublicationModel,
    element: HTMLElement | null,
  ) => void;
  /** The `initialPublicationId` (or `#<id>` / `?publicationId=`) deep
   *  link resolved to a mounted tile: fired once, after the initial scroll
   *  settled, so the host can open the same popover it opens on a click. */
  onInitialPublicationReady?: (
    publication: PublicationModel,
    element: HTMLElement,
  ) => void;
  getPublicationContext?: (
    publication: PublicationModel,
  ) => CalendarPublicationContext;
  highlightedAccountId?: string | null;
  highlightedVideoId?: string | null;
  highlightTrigger?: number;
  allowCreatePublication?: boolean;
  onCreatePublicationOnDay?: (date: Date) => void;
  showTimeSlotsOnHover?: boolean;
  isPublicationInActiveCalendarScope?: (
    publication: PublicationModel,
  ) => boolean;
  /** Schedule workspace only: mark posts that were already saved on entry. */
  showSavedScheduledStatus?: (publication: PublicationModel) => boolean;
  /** The post the host's open inspector is SHOWING: that tile wears the
   *  active surface. It follows the inspector's own pager, so it is not
   *  necessarily the tile that was clicked. Hosts without an inspector leave
   *  it out. */
  activePublicationId?: string | null;
  /**
   * A post the host wants brought into view — the inspector's pager naming
   * the post it just moved to. Everything that takes is this component's
   * job, because this component is the only place with the pieces: changing
   * the period when the post is not on this grid, waiting for its tile to
   * mount, scrolling the least that makes it visible, pulsing it on arrival.
   * The host never touches a tile. A one-shot instruction, not a controlled
   * value: it is handled once per `token`, so the same post can be asked for
   * twice and arrive twice.
   */
  revealRequest?: CalendarRevealRequest | null;
  /** Rendered in the header's right control group, before the nav buttons (e.g. the shorts picker). */
  titleAddon?: React.ReactNode;
  /** Wrap the existing action without changing its date/occupied-slot behavior. */
  wrapScheduleButton?: (button: React.ReactNode) => React.ReactNode;
  /** Pin below the global app header, or at the top of an embedded workspace. */
  stickyUnder?: "app-header" | "scrollport";
}

const INITIAL_SCROLL_STABILIZATION_DELAYS_MS = [50, 120, 260, 420] as const;
const PENDING_REVEAL_TIMEOUT_MS = 1000;

function parseDateValue(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string") {
    const parsedDate = parseISO(value);
    return isValid(parsedDate) ? parsedDate : null;
  }

  return null;
}

function parseDateFromDayElementId(dayElementId: string | null | undefined) {
  if (!dayElementId) return null;
  return parseDateValue(dayElementId.replace("day-", ""));
}

export default function Calendar({
  viewMode = "month",
  initialDate,
  initialPublicationId,
  publications: publicationsProp,
  occupancyPublications,
  isLoading: isLoadingProp,
  isPublicationDraggable,
  isPublicationDisabled,
  onPublicationScheduleChange,
  onPublicationDragStart,
  onPublicationClick,
  onInitialPublicationReady,
  getPublicationContext,
  highlightedAccountId: highlightedAccountIdProp,
  highlightedVideoId: highlightedVideoIdProp,
  highlightTrigger: highlightTriggerProp = 0,
  allowCreatePublication = true,
  onCreatePublicationOnDay,
  showTimeSlotsOnHover = true,
  isPublicationInActiveCalendarScope,
  showSavedScheduledStatus,
  activePublicationId,
  revealRequest,
  titleAddon,
  wrapScheduleButton,
  stickyUnder = "app-header",
  ...rest
}: CalendarProps & React.HTMLAttributes<HTMLDivElement>) {
  const modal = useContext(ModalContext);
  const isMobile = useMediaQuery("(max-width: 767px)", { initializeWithValue: false });
  // Phone always pages by week (agenda). Desktop keeps the caller's viewMode
  // (today always "month" — no host passes week).
  const navigateByWeek = isMobile || viewMode === "week";
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Parse initial date from query params or use provided initialDate
  const getInitialDate = useCallback(() => {
    // Embedded flows (e.g. bulk publish) pass a specific starting point
    // and publication id; prefer it over a stale URL query date.
    if (initialDate && initialPublicationId) {
      return initialDate;
    }

    // Check for currentDate in query params first
    const currentDateParam = (searchParams?.get("currentDate") as string) || "";
    if (currentDateParam) {
      const parsedDate = parseISO(currentDateParam);
      if (isValid(parsedDate)) {
        return parsedDate;
      }
    }

    // Fallback to provided initialDate or current date
    return initialDate || new Date();
  }, [initialDate, initialPublicationId, searchParams]);

  const [currentDate, setCurrentDate] = useState(getInitialDate);

  // Update URL when currentDate changes
  const updateURL = useCallback(
    (date: Date) => {
      const dateString = format(date, "yyyy-MM-dd");
      const newQuery = {
        ...Object.fromEntries(searchParams || []),
        currentDate: dateString,
      };

      // Remove currentDate from URL if it's today's date (clean URLs)
      const today = format(new Date(), "yyyy-MM-dd");
      if (dateString === today) {
        delete (newQuery as any).currentDate;
      }

      // Only update URL if it's actually different
      const currentQueryDate =
        (searchParams?.get("currentDate") as string) || "";
      const shouldUpdate =
        dateString === today
          ? currentQueryDate !== ""
          : currentQueryDate !== dateString;

      if (shouldUpdate) {
        const safePathname = pathname ?? "/";
        const newSearchParams = new URLSearchParams(newQuery);
        const nextQueryString = newSearchParams.toString();
        const nextUrl = nextQueryString
          ? `${safePathname}?${nextQueryString}`
          : safePathname;
        router.replace(nextUrl, { scroll: false });
      }
    },
    [pathname, router, searchParams],
  );

  // Tracks the latest currentDate so the `currentDate` URL-watcher effect
  // below (defined further down to sit after `scrollToDateOnNextFrame` for
  // TDZ reasons) can compare without re-subscribing on every date change.
  const currentDateRef = useRef(currentDate);
  useEffect(() => {
    currentDateRef.current = currentDate;
  }, [currentDate]);

  const [internalHighlightAccountId, setInternalHighlightAccountId] = useState<
    string | null
  >(null);
  const [internalHighlightTrigger, setInternalHighlightTrigger] = useState(0);
  const isStandalone = highlightedAccountIdProp === undefined;
  const effectiveHighlightAccountId = isStandalone
    ? internalHighlightAccountId
    : highlightedAccountIdProp;
  const effectiveHighlightVideoId = isStandalone
    ? null
    : (highlightedVideoIdProp ?? null);
  const effectiveHighlightTrigger = isStandalone
    ? internalHighlightTrigger
    : highlightTriggerProp;
  const handlePublicationHighlight = useCallback(
    (publication: PublicationModel) => {
      if (!isStandalone) return;
      if (publication.accountId) {
        setInternalHighlightAccountId(publication.accountId);
        setInternalHighlightTrigger((prev) => prev + 1);
      }
    },
    [isStandalone],
  );

  const [draggingPublication, setDraggingPublication] =
    useState<PublicationModel | null>(null);
  const [dragOverDayKey, setDragOverDayKey] = useState<string | null>(null);
  const [initialScheduledDate, setInitialScheduledDate] = useState<Date | null>(
    null,
  );
  const calendarGridRef = useRef<HTMLDivElement>(null);
  const calendarStickyRef = useRef<HTMLDivElement>(null);
  const initialScrollPerformedRef = useRef(false);
  const initialScrollStabilizationTimeoutsRef = useRef<number[]>([]);
  const publicationRefs = useRef<Map<string, HTMLElement>>(new Map());
  const pendingRevealRef = useRef<{
    publicationId: string;
    run: (element: HTMLElement) => void;
  } | null>(null);
  const pendingRevealTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const handledRevealTokenRef = useRef<number | null>(null);
  /** Cancels a pulse that is waiting for a glide to land. Held so the next
   *  jump can revoke it: its tile is no longer the one being looked at. */
  const revealPulseWaitRef = useRef<(() => void) | null>(null);
  // Set LAST — after the scroll is issued and after the active tint has
  // already committed — because the tile's className is one interpolated
  // string, and a later commit would rewrite it and drop the pulse class.
  const [revealPulse, setRevealPulse] = useState<{
    publicationId: string;
    token: number;
  } | null>(null);
  const onInitialPublicationReadyRef = useRef(onInitialPublicationReady);
  useEffect(() => {
    onInitialPublicationReadyRef.current = onInitialPublicationReady;
  }, [onInitialPublicationReady]);
  const initialPublicationReadyTimeoutsRef = useRef<
    ReturnType<typeof setTimeout>[]
  >([]);
  useEffect(() => {
    const timeouts = initialPublicationReadyTimeoutsRef.current;
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.length = 0;
      // StrictMode replays mount effects with refs preserved. A cancelled
      // initial reveal must be allowed to schedule again on that replay.
      initialScrollPerformedRef.current = false;
    };
  }, []);
  const dayRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const { data: fetchedPublications, isLoading: fetchedIsLoadingPublications } =
    useReadPublications();
  const publications = publicationsProp ?? fetchedPublications;
  const isUsingExternalPublications = publicationsProp !== undefined;
  const isLoadingPublications = isLoadingProp ?? fetchedIsLoadingPublications;
  // A tile drags only where a host owns the drop (the schedule shell's edit
  // face, which pins it into the session): the standalone calendar page is
  // a read surface — a click leads to the edit face, and no schedule write
  // happens outside a session and its Save.
  const getIsPublicationDraggable = useCallback(
    (publication: PublicationModel) => {
      if (!onPublicationScheduleChange || !isPublicationDraggable) {
        return false;
      }
      return isPublicationDraggable(publication);
    },
    [isPublicationDraggable, onPublicationScheduleChange],
  );
  const getIsPublicationDisabled = useCallback(
    (publication: PublicationModel) => {
      if (isPublicationDisabled) {
        return isPublicationDisabled(publication);
      }
      return false;
    },
    [isPublicationDisabled],
  );
  const setCurrentDateIfNeeded = useCallback((nextDate: Date) => {
    setCurrentDate((previousDate) => {
      return isSameDay(previousDate, nextDate) ? previousDate : nextDate;
    });
  }, []);
  const clearInitialScrollStabilizationTimeouts = useCallback(() => {
    initialScrollStabilizationTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    initialScrollStabilizationTimeoutsRef.current = [];
  }, []);
  const setDayRef = useCallback(
    (dayId: string, element: HTMLDivElement | null) => {
      if (element) {
        dayRefs.current.set(dayId, element);
      } else {
        dayRefs.current.delete(dayId);
      }
    },
    [],
  );
  const getDayElementByDate = useCallback((date: Date) => {
    const dayId = format(date, "yyyy-MM-dd");
    return (
      dayRefs.current.get(dayId) ??
      (document.getElementById(`day-${dayId}`) as HTMLDivElement | null)
    );
  }, []);
  const findScrollableAncestor = useCallback((element: HTMLElement | null) => {
    let current = element?.parentElement ?? null;
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const canScroll =
        /(auto|scroll|overlay)/.test(style.overflowY) &&
        current.scrollHeight > current.clientHeight;
      if (canScroll) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }, []);

  const scrollToDate = useCallback(
    (
      date: Date,
      options?: {
        behavior?: ScrollBehavior;
        retries?: number;
      },
    ) => {
      const { behavior = "smooth", retries = 0 } = options ?? {};
      const dateElement = getDayElementByDate(date);

      if (!dateElement) {
        if (retries > 0) {
          window.setTimeout(() => {
            scrollToDate(date, { behavior, retries: retries - 1 });
          }, 50);
        }
        return;
      }

      const grid = calendarGridRef.current;
      // Reveal the row divider too. The grid and its decorative
      // frame never own scrolling; use the workspace or document below.
      const rowBorderWidth = grid?.clientTop ?? 0;
      if (grid && retries > 0 && !grid.contains(dateElement)) {
        window.setTimeout(() => {
          scrollToDate(date, { behavior, retries: retries - 1 });
        }, 50);
        return;
      }

      const ancestorScrollContainer = findScrollableAncestor(dateElement);
      if (ancestorScrollContainer) {
        const ancestorRect = ancestorScrollContainer.getBoundingClientRect();
        const dateRect = dateElement.getBoundingClientRect();
        const stickyTopOffset = calendarStickyRef.current
          ? Number.parseFloat(
              window.getComputedStyle(calendarStickyRef.current).top || "0",
            ) || 0
          : 0;
        const stickyHeaderOffset =
          calendarStickyRef.current &&
          ancestorScrollContainer.contains(calendarStickyRef.current)
            ? (calendarStickyRef.current?.offsetHeight ?? 0) + stickyTopOffset
            : 0;
        const targetTop =
          dateRect.top -
          ancestorRect.top +
          ancestorScrollContainer.scrollTop -
          stickyHeaderOffset -
          rowBorderWidth;

        ancestorScrollContainer.scrollTo({
          top: Math.max(0, targetTop),
          behavior,
        });
        return;
      }

      window.scrollTo({
        top: Math.max(
          0,
          window.scrollY +
            dateElement.getBoundingClientRect().top -
            (calendarStickyRef.current?.offsetHeight ?? 0) -
            (calendarStickyRef.current
              ? Number.parseFloat(
                  window.getComputedStyle(calendarStickyRef.current).top || "0",
                ) || 0
              : 0) -
            rowBorderWidth,
        ),
        behavior,
      });
    },
    [findScrollableAncestor, getDayElementByDate],
  );
  const scrollToDateOnNextFrame = useCallback(
    (date: Date) => {
      requestAnimationFrame(() => {
        scrollToDate(date, { retries: 10 });
      });
    },
    [scrollToDate],
  );
  const stabilizeInitialDayCellScroll = useCallback(
    (date: Date) => {
      // Re-apply alignment over several frames to resist layout shifts
      // (font loading, async data merge, publication card measurements).
      clearInitialScrollStabilizationTimeouts();
      scrollToDate(date, { behavior: "auto", retries: 10 });

      INITIAL_SCROLL_STABILIZATION_DELAYS_MS.forEach((delayMs) => {
        const timeoutId = window.setTimeout(() => {
          requestAnimationFrame(() => {
            scrollToDate(date, { behavior: "auto", retries: 10 });
          });
        }, delayMs);
        initialScrollStabilizationTimeoutsRef.current.push(timeoutId);
      });
    },
    [clearInitialScrollStabilizationTimeouts, scrollToDate],
  );

  // Sync state (and scroll) to the `currentDate` query param on external URL
  // changes — e.g. `router.push` after submitting the Schedule posts dialog
  // while already on /calendar. The initial-scroll effect only fires once
  // per mount, so this handles every subsequent URL-driven date change.
  useEffect(() => {
    if (initialDate && initialPublicationId) return;

    const currentDateParam = searchParams?.get("currentDate") || "";
    if (!currentDateParam) return;

    const parsedDate = parseISO(currentDateParam);
    if (!isValid(parsedDate)) return;
    if (isSameDay(parsedDate, currentDateRef.current)) return;

    setCurrentDate(parsedDate);
    if (initialScrollPerformedRef.current) {
      scrollToDateOnNextFrame(parsedDate);
    }
  }, [
    initialDate,
    initialPublicationId,
    searchParams,
    scrollToDateOnNextFrame,
  ]);

  // Navigation handler for consistent behavior across all navigation buttons
  const handleNavigation = useCallback(
    (targetDate: Date, shouldScrollToPublication = true) => {
      // User navigation should always win over any delayed initial auto-scroll.
      initialScrollPerformedRef.current = true;
      clearInitialScrollStabilizationTimeouts();

      const isSameTargetDay = isSameDay(currentDate, targetDate);

      // If we're already on the exact target day (common for Today),
      // skip redundant state updates to avoid scroll anchoring jitter.
      if (!shouldScrollToPublication && isSameTargetDay) {
        updateURL(targetDate);
        scrollToDateOnNextFrame(targetDate);
        return;
      }

      setCurrentDateIfNeeded(targetDate);
      updateURL(targetDate);

      // If we shouldn't scroll to publication (like for "Today" button), just scroll to the date
      if (!shouldScrollToPublication) {
        scrollToDateOnNextFrame(targetDate);
        return;
      }

      // Find the first publication in the target period for better UX
      const targetPublication = publications?.findLast((pub) => {
        if (!pub.scheduledAt) return false;

        if (navigateByWeek) {
          const weekStart = startOfWeek(targetDate, { weekStartsOn: 1 });
          const weekEnd = endOfWeek(targetDate, { weekStartsOn: 1 });
          return pub.scheduledAt >= weekStart && pub.scheduledAt <= weekEnd;
        }
        return isSameMonth(pub.scheduledAt, targetDate);
      });

      // Use requestAnimationFrame for smoother scrolling
      scrollToDateOnNextFrame(targetPublication?.scheduledAt ?? targetDate);
    },
    [
      clearInitialScrollStabilizationTimeouts,
      currentDate,
      navigateByWeek,
      publications,
      scrollToDateOnNextFrame,
      setCurrentDateIfNeeded,
      updateURL,
    ],
  );

  const cancelRevealPulseWait = useCallback(() => {
    revealPulseWaitRef.current?.();
    revealPulseWaitRef.current = null;
  }, []);

  const clearPendingReveal = useCallback(() => {
    if (pendingRevealTimeoutRef.current !== null) {
      clearTimeout(pendingRevealTimeoutRef.current);
      pendingRevealTimeoutRef.current = null;
    }
    pendingRevealRef.current = null;
  }, []);

  const armPendingReveal = useCallback(
    (publicationId: string, run: (element: HTMLElement) => void) => {
      clearPendingReveal();
      pendingRevealRef.current = { publicationId, run };
      // A CANCEL, not a poll: it fires once, and all it does is forget a
      // request whose post never rendered (its row left the list between the
      // ask and the commit), so a stale pending cannot fire on some unrelated
      // later mount. Nothing here retries.
      pendingRevealTimeoutRef.current = setTimeout(
        clearPendingReveal,
        PENDING_REVEAL_TIMEOUT_MS,
      );
    },
    [clearPendingReveal],
  );

  const setPublicationRef = useCallback(
    (publicationId: string, element: HTMLElement | null) => {
      if (element) {
        publicationRefs.current.set(publicationId, element);
        // This is the ONLY place the component learns a tile exists. A month
        // change remounts every day cell (`key={day.toString()}`) and React
        // detaches the outgoing refs and attaches the incoming ones inside one
        // commit, so the destination tile's attach IS the signal that the new
        // period is on screen; nothing else pushes
        // (`onInitialPublicationReady` fires at most once per mount).
        //
        // One rAF, deliberately: this runs inside React's commit, where the
        // rest of the new period has not attached its refs yet and where
        // scrolling fights React's own scroll handling. A FRAME, not a guessed
        // delay, so there is nothing to tune.
        //
        // Note PublicationItem's ref is an inline arrow, so this callback fires
        // on EVERY render of every tile, not on mount — which is harmless only
        // because a pending reveal is armed exclusively when the tile is
        // ABSENT. Do not relax that.
        const pending = pendingRevealRef.current;
        if (pending?.publicationId === publicationId) {
          clearPendingReveal();
          requestAnimationFrame(() => {
            if (element.isConnected) pending.run(element);
          });
        }
      } else {
        publicationRefs.current.delete(publicationId);
      }
    },
    [clearPendingReveal],
  );
  const getInitialPublicationId = useCallback(() => {
    return (
      initialPublicationId ||
      window.location.hash.split("#")[1] ||
      searchParams?.get("publicationId")
    );
  }, [initialPublicationId, searchParams]);
  const resolveInitialScrollDate = useCallback(
    (publicationId: string | null | undefined) => {
      const publicationElement = publicationId
        ? publicationRefs.current.get(publicationId)
        : null;
      const targetPublication = publicationId
        ? publications?.find((publication) => publication.id === publicationId)
        : null;
      const publicationElementDate = parseDateFromDayElementId(
        publicationElement?.closest<HTMLElement>("[id^='day-']")?.id,
      );
      const publicationDate = parseDateValue(targetPublication?.scheduledAt);
      return (
        publicationDate || publicationElementDate || initialDate || currentDate
      );
    },
    [currentDate, initialDate, publications],
  );

  useEffect(() => {
    if (initialScrollPerformedRef.current) return;

    const publicationId = getInitialPublicationId();

    // If a specific publication is requested, wait until we have publication data
    // for internally-fetched calendars. External/publication-provided calendars
    // (bulk flow) can scroll immediately.
    const shouldWaitForPublicationData =
      !!publicationId &&
      (publications === undefined ||
        (!isUsingExternalPublications && isLoadingPublications));
    if (shouldWaitForPublicationData) return;

    const targetDate = resolveInitialScrollDate(publicationId);
    setCurrentDateIfNeeded(targetDate);
    stabilizeInitialDayCellScroll(targetDate);
    initialScrollPerformedRef.current = true;
    // The deep link's tile, once it is mounted: poll on the scroll
    // stabilization's own schedule (plus one late tick) and hand it to the
    // host exactly once. An unknown id fires nothing.
    const targetPublication = publicationId
      ? publications?.find((publication) => publication.id === publicationId)
      : null;
    if (targetPublication && onInitialPublicationReadyRef.current) {
      let fired = false;
      [...INITIAL_SCROLL_STABILIZATION_DELAYS_MS, 700].forEach((delayMs) => {
        const timeout = setTimeout(() => {
          if (fired) return;
          const element = publicationRefs.current.get(targetPublication.id);
          if (!element) return;
          fired = true;
          onInitialPublicationReadyRef.current?.(targetPublication, element);
        }, delayMs);
        initialPublicationReadyTimeoutsRef.current.push(timeout);
      });
    }
  }, [
    getInitialPublicationId,
    isUsingExternalPublications,
    isLoadingPublications,
    publications,
    resolveInitialScrollDate,
    setCurrentDateIfNeeded,
    stabilizeInitialDayCellScroll,
  ]);
  useEffect(() => {
    return () => {
      clearInitialScrollStabilizationTimeouts();
      clearPendingReveal();
      cancelRevealPulseWait();
    };
  }, [
    cancelRevealPulseWait,
    clearInitialScrollStabilizationTimeouts,
    clearPendingReveal,
  ]);

  /**
   * The band of the viewport a tile can actually be READ in, and the scroller
   * that moves it. MEASURED off refs this component already holds, never
   * probed with `elementFromPoint`: the inspector panel is `position:fixed`
   * and portaled to the body, so a probe finds the PANEL and asks for a scroll
   * no vertical movement can satisfy.
   *
   * The main calendar scrolls with the document; embedded calendars use
   * their workspace's scrollport. Both keep the month and weekday labels
   * sticky, so revealed tiles must clear that strip.
   */
  const resolveRevealViewport = useCallback(
    (
      element: HTMLElement,
    ): { scroller: HTMLElement | null; band: TileRevealBand } | null => {
      const scroller = findScrollableAncestor(element);
      const scrollerRect = scroller?.getBoundingClientRect();
      const portTop = scrollerRect ? scrollerRect.top : 0;
      const portBottom = scrollerRect ? scrollerRect.bottom : window.innerHeight;
      const stickyBottom =
        calendarStickyRef.current?.getBoundingClientRect().bottom ??
        Number.NEGATIVE_INFINITY;
      const band = {
        top: Math.max(portTop, stickyBottom),
        bottom: portBottom - CALENDAR_FRAME_BOTTOM_GAP,
      };
      return band.bottom - band.top < 1 ? null : { scroller, band };
    },
    [findScrollableAncestor],
  );

  /** The tile, from the ref map first. The fallback query is SCOPED to this
   *  calendar's grid so a document-wide query cannot match a
   *  `data-publication-id` that is not on this grid. */
  const resolveTileElement = useCallback((publicationId: string) => {
    const mapped = publicationRefs.current.get(publicationId);
    if (mapped?.isConnected) return mapped;
    return (
      calendarGridRef.current?.querySelector<HTMLElement>(
        `[data-publication-id="${publicationId}"]`,
      ) ?? null
    );
  }, []);

  const startReveal = useCallback(
    (request: CalendarRevealRequest) => {
      // A jump supersedes the one before it: a pulse still waiting on the
      // previous glide would land on a tile the inspector has already left.
      cancelRevealPulseWait();
      // Reveal outranks the mount-time auto-scroll on EVERY path, not only a
      // period change. List → "Show in calendar" mounts the grid and reveals
      // in the same tick; a same-month target would otherwise lose to the
      // initial scroll's top-align a frame later.
      initialScrollPerformedRef.current = true;
      clearInitialScrollStabilizationTimeouts();
      const finishReveal = (
        element: HTMLElement,
        behavior: ScrollBehavior,
      ) => {
        const viewport = resolveRevealViewport(element);
        if (!viewport) return;
        const reduced = prefersReducedMotion();
        const plan = planTileReveal(
          element.getBoundingClientRect(),
          viewport.band,
        );
        if (plan.kind === "scroll") {
          revealCalendarTile(
            viewport.scroller,
            plan.delta,
            reduced ? "auto" : behavior,
          );
        }
        // The pulse belongs to the JUMP, not to the scroll it may or may not
        // have needed. A navigation step lands on a tile somewhere else on the
        // grid while the panel stays put, and the two commonest steps —
        // between two posts of the same day cell — move nothing at all: the
        // panel's content swaps and the grid says nothing about which of the
        // stacked tiles it is now showing. So every reveal bumps its
        // destination, and the tint below is what KEEPS saying it afterwards.
        // Reduced motion skips the bump and lands the scroll instantly;
        // deciding that here means PublicationItem needs no gate of its own.
        if (reduced) return;
        const pulse = () =>
          setRevealPulse({
            publicationId: request.publicationId,
            token: request.token,
          });
        // A glide and a bump played together read as one blurred movement, and
        // the bump is over before the tile has arrived. So when the grid
        // travels, the pulse waits for it to come to rest; a period change
        // lands instantly ("auto") and a tile already in the band never moved,
        // and both of those pulse on the spot.
        if (plan.kind !== "scroll" || behavior === "auto") {
          pulse();
          return;
        }
        cancelRevealPulseWait();
        revealPulseWaitRef.current = onScrollSettled(viewport.scroller, () => {
          revealPulseWaitRef.current = null;
          pulse();
        });
      };

      // Guarded before anything touches the period: `days` runs
      // `eachDayOfInterval(startOfWeek(startOfMonth(currentDate)) …)`, which
      // throws a RangeError on an Invalid Date, so a post with no usable
      // date reaching `setCurrentDate` is a white screen, not a missed reveal.
      // Such a post has no tile on any grid anyway; the pager has already
      // switched to it.
      const row = publications?.find((pub) => pub.id === request.publicationId);
      const date = toValidDate(row?.scheduledAt);
      if (!date) {
        clearPendingReveal();
        return;
      }

      // Asked of the DOM, not of a date range: the month view always draws six
      // full weeks, so a post a few days either side of the boundary already
      // has a real tile and needs no period change at all. This is also what
      // tells "another month" apart from "a row this grid filters out".
      const needsPeriodChange = !getDayElementByDate(date);

      if (!needsPeriodChange) {
        const tile = resolveTileElement(request.publicationId);
        if (tile) {
          finishReveal(tile, "smooth");
          return;
        }
        // Fall through: the day cell is drawn but the tile has not registered
        // yet.
      }

      // A step INSIDE the drawn period glides; a period change lands
      // instantly — there is nothing continuous to animate between two
      // unrelated layouts, and the carried-over scrollTop means nothing in the
      // new month. The pulse is what says "here it is".
      armPendingReveal(request.publicationId, (element) =>
        finishReveal(element, needsPeriodChange ? "auto" : "smooth"),
      );

      if (!needsPeriodChange) return;
      // `setCurrentDateIfNeeded` first, `updateURL` second, is what keeps the
      // inbound `currentDate` watcher quiet: `currentDateRef`'s mirror effect
      // is declared above it, so by the time the replaced URL lands the ref
      // already holds the target day and the watcher's `isSameDay` guard
      // returns before it can fire `scrollToDateOnNextFrame` — a smooth
      // full-viewport TOP-ALIGN under a pinned panel. Same order
      // `handleNavigation` relies on.
      //
      // The URL is written for a reason, not out of habit: leave it stale and
      // the very next search-param change from anywhere snaps the month back,
      // and on the global page `returnDate` for "Edit post" would point at the
      // month the user paged away from. `handleNavigation` itself is
      // deliberately NOT reused — it is user navigation and jumps to the LAST
      // post of the period it lands in, a full-viewport move we would
      // immediately have to undo.
      setCurrentDateIfNeeded(date);
      updateURL(date);
    },
    [
      armPendingReveal,
      cancelRevealPulseWait,
      clearInitialScrollStabilizationTimeouts,
      clearPendingReveal,
      getDayElementByDate,
      publications,
      resolveRevealViewport,
      resolveTileElement,
      setCurrentDateIfNeeded,
      updateURL,
    ],
  );

  // Layout, not passive, so the same-period scroll lands in the same frame as
  // the tint with no intermediate paint.
  //
  // The token latch is load-bearing beyond StrictMode's double-invoke:
  // `startReveal` depends on `updateURL`, which re-identifies on every
  // `searchParams` change, and without the latch an unrelated URL change would
  // re-run the reveal and re-scroll.
  //
  // The null branch is the cancel: the inspector closing must not leave a
  // pending reveal armed, or a cross-month step followed straight by Escape
  // would scroll the grid and pulse a tile with no panel open.
  useLayoutEffect(() => {
    if (!revealRequest) {
      clearPendingReveal();
      setRevealPulse((previous) => (previous === null ? previous : null));
      handledRevealTokenRef.current = null;
      return;
    }
    if (handledRevealTokenRef.current === revealRequest.token) return;
    handledRevealTokenRef.current = revealRequest.token;
    startReveal(revealRequest);
  }, [revealRequest, startReveal, clearPendingReveal]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const days = useMemo(() => {
    if (navigateByWeek) {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: weekStart, end: weekEnd });
    }
    const firstOfMonth = startOfMonth(currentDate);
    const lastOfMonth = endOfMonth(currentDate);
    const firstDayOfGrid = startOfWeek(firstOfMonth, { weekStartsOn: 1 });
    const lastDayOfGrid = endOfWeek(lastOfMonth, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: firstDayOfGrid, end: lastDayOfGrid });
  }, [currentDate, navigateByWeek]);

  // No mid-drag preview remap: publications only move on drop, so slot cells
  // never grow or shift while dragging.
  const calendarPublications = publications;

  const publicationIndexById = useMemo(() => {
    const indexById = new Map<string, number>();
    calendarPublications?.forEach((publication, index) => {
      indexById.set(publication.id, index);
    });
    return indexById;
  }, [calendarPublications]);

  const registerSlotCell = useUniformSlotCells();

  // For each day, the set of slot bands used anywhere in that day's week —
  // determines which cells take part in uniform slot sizing.
  const weekActiveSlotIdsByDayId = useMemo(() => {
    const activeByDay = new Map<string, Set<string>>();
    if (!calendarPublications) return activeByDay;

    const slotIdsByDayId = new Map<string, Set<string>>();
    calendarPublications.forEach((publication) => {
      if (!publication.scheduledAt) return;
      const date = new Date(publication.scheduledAt);
      const hour = date.getHours();
      const slot = CALENDAR_SLOTS.find(
        (s) => hour >= s.startHour && hour <= s.endHour,
      );
      if (!slot) return;
      const dayId = format(date, "yyyy-MM-dd");
      let slotIds = slotIdsByDayId.get(dayId);
      if (!slotIds) {
        slotIds = new Set();
        slotIdsByDayId.set(dayId, slotIds);
      }
      slotIds.add(slot.id);
    });

    for (let i = 0; i < days.length; i += 7) {
      const weekDays = days.slice(i, i + 7);
      const weekActive = new Set<string>();
      weekDays.forEach((day) => {
        slotIdsByDayId
          .get(format(day, "yyyy-MM-dd"))
          ?.forEach((slotId) => weekActive.add(slotId));
      });
      weekDays.forEach((day) => {
        activeByDay.set(format(day, "yyyy-MM-dd"), weekActive);
      });
    }
    return activeByDay;
  }, [calendarPublications, days]);

  const [dragTarget, setDragTarget] = useState<CalendarPlacementTarget | null>(null);
  const creationBlockedAtPointerDown = useRef(false);
  const handleCreatePublicationOnDay = useCallback((date: Date) => {
    // A dismissing click must stay blocked even if the inspector closes
    // before the calendar receives the click event.
    if (activePublicationId || creationBlockedAtPointerDown.current) return;
    onCreatePublicationOnDay?.(date);
  }, [activePublicationId, onCreatePublicationOnDay]);
  const resolvedDragTarget = useRef<CalendarPlacementTarget | null>(null);
  const pointerCoordinates = useRef<{ x: number; y: number } | null>(null);
  const resetDragState = useCallback(() => {
    resolvedDragTarget.current = null;
    setDragTarget(null);
    setDragOverDayKey(null);
    setDraggingPublication(null);
    setInitialScheduledDate(null);
  }, []);

  // The card just rescheduled by drag-drop: it remounts in its new slot, and
  // that remount must not replay the highlight bump (see PublicationItem).
  const lastDroppedPublicationIdRef = useRef<string | null>(null);
  const isRecentlyDropped = useCallback(
    (publicationId: string) =>
      lastDroppedPublicationIdRef.current === publicationId,
    [],
  );

  const handleDragStart = (event: DragStartEvent) => {
    lastDroppedPublicationIdRef.current = null;
    const { publication, isDisabled } = event.active.data.current as {
      publication: PublicationModel;
      isDisabled?: boolean;
    };

    if (isDisabled) {
      setTimeout(() => {
        resetDragState();
        // toast.error("Cannot reschedule posts that have already been posted");
      }, 0);
      return;
    }

    setInitialScheduledDate(publication.scheduledAt);
    setDraggingPublication(publication);
    onPublicationDragStart?.(publication);
  };

  const handleDragOver = (event: DragMoveEvent) => {
    const data = event.over?.data.current;
    const point = pointerCoordinates.current;
    const target = data?.isTimeSlot && point ? data.resolveTarget?.(point.y) ?? null : null;
    resolvedDragTarget.current = target;
    setDragTarget(previous => previous?.slotKey === target?.slotKey && previous?.time === target?.time && previous?.top === target?.top && previous?.original === target?.original ? previous : target);
    setDragOverDayKey(data?.day ? format(data.day, "yyyy-MM-dd") : null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { publication, isDisabled } = event.active.data.current as { publication: PublicationModel; isDisabled?: boolean };
    // Commit the exact suggestion shown by both previews. If it expired
    // during the drag, reject it instead of silently choosing another time.
    const target = event.over?.id === resolvedDragTarget.current?.slotKey ? resolvedDragTarget.current : null;
    resetDragState();
    if (isDisabled || !target || target.original || target.time <= Date.now() || !onPublicationScheduleChange) return;
    if (publication.scheduledAt && target.time === new Date(publication.scheduledAt).getTime()) return;
    lastDroppedPublicationIdRef.current = publication.id;
    try {
      await onPublicationScheduleChange(publication, new Date(target.time));
    } catch (error) {
      toast.error("Failed to reschedule post");
      console.error("Error updating post schedule:", error);
    }
  };

  return (
    <CalendarContext.Provider
      value={{
        draggingPublication,
        dragTarget,
        onCreatePublicationOnDay: !activePublicationId && onCreatePublicationOnDay ? handleCreatePublicationOnDay : null,
        canMovePublications: !!onPublicationScheduleChange,
        initialScheduledDate,
        onPublicationClick: onPublicationClick ?? null,
        onPublicationHighlight: handlePublicationHighlight,
        getPublicationContext: getPublicationContext ?? null,
        highlightedAccountId: effectiveHighlightAccountId ?? null,
        highlightedVideoId: effectiveHighlightVideoId,
        highlightTrigger: effectiveHighlightTrigger,
        isPublicationInActiveCalendarScope:
          isPublicationInActiveCalendarScope ?? null,
        showSavedScheduledStatus: showSavedScheduledStatus ?? null,
        activePublicationId: activePublicationId ?? null,
        revealedPublicationId: revealPulse?.publicationId ?? null,
        revealPulseToken: revealPulse?.token ?? 0,
        registerSlotCell,
        publicationIndexById,
        isRecentlyDropped,
      }}
    >
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragMove={handleDragOver}
        // Escape mid-drag: clear the origin ghost and hovered-day labels too
        onDragCancel={resetDragState}
        collisionDetection={(args) => {
          pointerCoordinates.current = args.pointerCoordinates;
          const collisions = pointerWithin(args);
          const point = args.pointerCoordinates;
          if (collisions.length || !point) return collisions;

          // The entire day is a drop area. Its header and outer padding
          // delegate to the nearest band, retaining that band's resolver
          // for preview, eligibility and the final drop. Never reach into
          // another day or accept a pointer outside the calendar cells.
          const hoveredDay = [...dayRefs.current].find(([, element]) => {
            const rect = element.getBoundingClientRect();
            return point.x >= rect.left && point.x < rect.right &&
              point.y >= rect.top && point.y < rect.bottom;
          })?.[0];
          if (!hoveredDay) return [];
          const bands = args.droppableContainers.flatMap(container => {
            const data = container.data.current;
            const rect = args.droppableRects.get(container.id);
            if (!data?.isTimeSlot || !rect || format(data.day, "yyyy-MM-dd") !== hoveredDay) return [];
            return [{ top: rect.top, bottom: rect.bottom, container }];
          });
          const nearest = nearestCalendarBand(bands, point.y);
          return nearest ? [{ id: nearest.container.id, data: { droppableContainer: nearest.container, value: 0 } }] : [];
        }}
        sensors={sensors}
      >
        <div
          {...rest}
          onPointerDownCapture={event => {
            creationBlockedAtPointerDown.current = !!activePublicationId;
            rest.onPointerDownCapture?.(event);
          }}
          onClickCapture={event => {
            // Keyboard activation is a new gesture without a pointerdown.
            if (event.detail === 0) creationBlockedAtPointerDown.current = false;
            rest.onClickCapture?.(event);
          }}
          // Both hosts scroll away this 24px gap, leaving the sticky strip's
          // 8px padding. `clip` avoids creating another sticky scrollport;
          // the 4px content-box gutter keeps the now-bar dot that sits on
          // the frame's left border from being cropped.
          className={`${rest.className ?? ""} flex flex-col relative min-w-0 pt-6 overflow-x-clip box-content -ml-1 pl-1`}
        >
          {/* Actions and date navigation share the right edge, beside the
              title on phones too. */}
          <div
            ref={calendarStickyRef}
            // Match Analytics at rest (24px + 8px), then compact equally
            // in the document and the embedded schedule workspace. The
            // -ml-1 pl-1 covers the root's dot gutter with the white band.
            className={`flex shrink-0 flex-col -ml-1 pl-1 pt-2 z-20 sticky bg-white ${
              stickyUnder === "scrollport" ? "top-0" : "top-12"
            }`}
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-3 pb-4 md:gap-x-3">
              <Typography
                variant="h3"
                component="h2"
                className="whitespace-nowrap max-md:text-2xl"
              >
                {format(currentDate, "MMMM")}{" "}
                <span className="!font-normal text-slate-500">
                  {format(currentDate, "yyyy")}
                </span>
              </Typography>
              <div className="ml-auto flex flex-wrap items-center justify-end gap-1 md:gap-2">
                <div
                  role="group"
                  aria-label="Calendar navigation"
                  className="flex shrink-0 items-center gap-1 md:gap-2"
                >
                  <Button
                    variant="secondary"
                    size="m"
                    aria-label={navigateByWeek ? "Previous week" : "Previous month"}
                    onClick={() => handleNavigation(
                      navigateByWeek
                        ? subWeeks(currentDate, 1)
                        : startOfMonth(subMonths(currentDate, 1)),
                    )}
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="m"
                    className="px-2 md:px-3"
                    onClick={() => handleNavigation(new Date(), false)}
                  >
                    Today
                  </Button>
                  <Button
                    variant="secondary"
                    size="m"
                    aria-label={navigateByWeek ? "Next week" : "Next month"}
                    onClick={() => handleNavigation(
                      navigateByWeek
                        ? addWeeks(currentDate, 1)
                        : startOfMonth(addMonths(currentDate, 1)),
                    )}
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </Button>
                </div>
                {(titleAddon || allowCreatePublication) && (
                  <div className="flex items-center gap-2">
                    {titleAddon}
                    {allowCreatePublication && (wrapScheduleButton ?? ((button: React.ReactNode) => button))(
                      <Button
                        variant="primary"
                        size="m"
                        onClick={() => {
                          const occupiedSlots = createOccupiedSlotSet(
                            (occupancyPublications ?? publications ?? [])
                              .map((publication) => publication.scheduledAt)
                              .filter(
                                (scheduledAt): scheduledAt is Date =>
                                  scheduledAt instanceof Date &&
                                  !Number.isNaN(scheduledAt.getTime()),
                              ),
                          );
                          const nextRecommendedSlot = findNextRecommendedTimeSlot(
                            currentDate,
                            occupiedSlots,
                          );
                          modal.open(
                            <SelectVideoPublicationForm
                              defaultDate={nextRecommendedSlot}
                              from="calendar"
                              returnDate={format(currentDate, "yyyy-MM-dd")}
                            />,
                            {
                              className: "h-full max-w-3xl",
                              disabledPadding: true,
                            },
                          );
                        }}
                      >
                        Schedule
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
            {!isMobile && (
              <div className="grid grid-cols-7 bg-white">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                  (day) => (
                    <Typography
                      key={day}
                      variant="subtitle2"
                      className="text-right py-2 pl-2 pr-4 text-slate-500"
                    >
                      {day}
                    </Typography>
                  ),
                )}
              </div>
            )}
          </div>

          <div
            ref={calendarGridRef}
            className={`flex flex-col relative min-w-0 border border-slate-150 ${BUTTON_STYLE.m.radius}`}
          >
            <CalendarViewportFrame headerRef={calendarStickyRef}>
            {isMobile ? (
              <div className="flex flex-col min-w-0 [&>div:last-child]:border-b-0">
                {days.map((day) => {
                  const dayId = format(day, "yyyy-MM-dd");
                  const dayPublications = calendarPublications?.filter((pub) =>
                    isSameDay(new Date(pub.scheduledAt ?? ""), day),
                  );

                  return (
                    <DroppableDay
                      key={day.toString()}
                      day={day}
                      dayId={dayId}
                      dayPublications={dayPublications ?? []}
                      isCurrentMonth={true}
                      draggingPublicationId={draggingPublication?.id || null}
                      isDragging={!!draggingPublication}
                      allowCreatePublication={allowCreatePublication}
                      isPublicationDraggable={getIsPublicationDraggable}
                      isPublicationDisabled={getIsPublicationDisabled}
                      setPublicationRef={setPublicationRef}
                      setDayRef={setDayRef}
                      isDayDragOver={dragOverDayKey === dayId}
                      showTimeSlotsOnHover={showTimeSlotsOnHover}
                      layout="agenda"
                      id={`day-${dayId}`}
                    />
                  );
                })}
              </div>
            ) : (
              <div
                className={`grid grid-cols-7 auto-rows-auto [&>div:nth-child(7n)]:border-r-0 [&>div:nth-last-child(-n+7)]:border-b-0 ${
                  viewMode === "week" ? "grid-rows-1" : ""
                }`}
              >
                {days.map((day) => {
                  const dayId = format(day, "yyyy-MM-dd");
                  const dayPublications = calendarPublications?.filter((pub) =>
                    isSameDay(new Date(pub.scheduledAt ?? ""), day),
                  );

                  return (
                    <DroppableDay
                      key={day.toString()}
                      day={day}
                      dayId={dayId}
                      dayPublications={dayPublications ?? []}
                      isCurrentMonth={
                        viewMode === "month"
                          ? isSameMonth(day, currentDate)
                          : true
                      }
                      draggingPublicationId={draggingPublication?.id || null}
                      isDragging={!!draggingPublication}
                      allowCreatePublication={allowCreatePublication}
                      isPublicationDraggable={getIsPublicationDraggable}
                      isPublicationDisabled={getIsPublicationDisabled}
                      setPublicationRef={setPublicationRef}
                      setDayRef={setDayRef}
                      isDayDragOver={dragOverDayKey === dayId}
                      showTimeSlotsOnHover={showTimeSlotsOnHover}
                      weekActiveSlotIds={weekActiveSlotIdsByDayId.get(dayId)}
                      layout="grid"
                      id={`day-${dayId}`}
                    />
                  );
                })}
              </div>
            )}
            </CalendarViewportFrame>
          </div>
        </div>

        <DragOverlay
          dropAnimation={{
            duration: 100,
            easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
          }}
          style={{
            cursor: "grabbing",
            // The floating copy can outlive release during its animation;
            // it must not intercept the next click or open an inspector.
            pointerEvents: "none",
          }}
        >
          {draggingPublication && (
            <PublicationItem
              publication={dragTarget ? { ...draggingPublication, scheduledAt: new Date(dragTarget.time) } : draggingPublication}
              isInOverlay={true}
            />
          )}
        </DragOverlay>
      </DndContext>
    </CalendarContext.Provider>
  );
}
